import { z } from 'zod';
import { useClinicSettings } from '../store/clinic-settings';
import { isValidPhoneNumber, CountryCode } from 'libphonenumber-js';

// Validation schema for patient data
export const patientSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
  dateOfBirth: z.date(),
  gender: z.enum(['male', 'female']),
  email: z.string().optional().transform((val) => val === '' ? undefined : val).pipe(
    z.string().email('Invalid email address').optional()
  ),
  phone: z.string().optional().refine((val: string | undefined): val is string => {
    if (!val) return true;
    const country = useClinicSettings.getState().country as CountryCode;
    try {
      return isValidPhoneNumber(val, country);
    } catch {
      return false;
    }
  }, 'Invalid phone number format'),
  address: z.object({
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string().optional(),
  }),
  insuranceInfo: z.object({
    provider: z.string(),
    policyNumber: z.string(),
    groupNumber: z.string().optional(),
  }),
  medicalHistory: z.string(),
  allergies: z.string(),
  medications: z.array(z.object({
    name: z.string(),
    dosage: z.string(),
    frequency: z.string(),
  })),
  status: z.enum(['active', 'inactive']),
  lastVisit: z.string().optional(),
});

export type Patient = z.infer<typeof patientSchema>;
